import React, { useMemo } from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Alert, Box, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useGetTicketRiskHistory } from '~/hooks/apis/support/useTicketRiskHistory';

import type { TicketRiskHistoryPoint, TicketRiskScoreDirection } from '~/types/ticketRisk';

import {
  EMPTY_VALUE,
  RISK_TIER_COLORS,
  bandToTier,
  formatDateTime,
  formatShortDate,
  formatShortDateTime,
  toTimestamp,
} from './ticketRiskRows';

const CHART_HEIGHT = 200;
const AXIS_FONT_SIZE = 11;
/** Pixels recharts must fit between two ticks before it drops one. */
const AXIS_MIN_TICK_GAP = 24;
const SCORE_AXIS_MAX = 100;
const DOT_RADIUS = 4;
const ACTIVE_DOT_RADIUS = 6;

/** Rising means the ticket is getting worse, so it takes the alarming colour, not the healthy one. */
const DIRECTION_STYLES: Record<TicketRiskScoreDirection, { label: string; color: 'error' | 'success' | 'default' }> = {
  rising: { label: 'Rising', color: 'error' },
  falling: { label: 'Falling', color: 'success' },
  flat: { label: 'Flat', color: 'default' },
  new: { label: 'First run', color: 'default' },
};

interface ChartPoint {
  /** Epoch ms of the run, so the axis spaces points by real time rather than run count. */
  timestamp: number;
  score: number;
  hardScore: number;
  llmScore: number | null;
  point: TicketRiskHistoryPoint;
}

const formatDelta = (delta: number | null) => {
  if (delta == null) {
    return 'no previous run';
  }

  return delta === 0 ? 'no change' : `${delta > 0 ? '+' : ''}${delta} on the previous run`;
};

interface Props {
  ticketId: string;
}

/**
 * The ticket's blended score across every scoring run, oldest first, with the deterministic
 * and LLM legs behind it so a jump can be attributed to the facts or to the language.
 */
export default function TicketScoreHistory({ ticketId }: Props) {
  const theme = useTheme();
  const { data: history, isLoading, error } = useGetTicketRiskHistory({ ticketId });

  const { chartData, tickFormatter } = useMemo(() => {
    const data = (history?.points ?? [])
      .map((point) => ({
        timestamp: toTimestamp(point.computedAt),
        score: point.score,
        hardScore: point.hardScore,
        llmScore: point.llmScore,
        point,
      }))
      .filter((entry): entry is ChartPoint => entry.timestamp !== null);
    // The job runs several times a day, so day-only labels would repeat and hide the movement.
    const days = new Set(data.map((entry) => formatShortDate(entry.timestamp)));
    const formatter = days.size === data.length ? formatShortDate : formatShortDateTime;

    return { chartData: data, tickFormatter: (value: number) => formatter(value) };
  }, [history]);

  if (isLoading) {
    return <Skeleton variant="rounded" height={CHART_HEIGHT} />;
  }

  if (error) {
    const isMissing = (error as { status?: number }).status === 404;

    return (
      <Alert severity={isMissing ? 'info' : 'warning'}>
        {isMissing
          ? 'No scoring history yet — this ticket was scored for the first time in the latest run.'
          : 'Could not load the score history for this ticket.'}
      </Alert>
    );
  }

  if (!history || chartData.length === 0) {
    return <Alert severity="info">No scoring history yet for this ticket.</Alert>;
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Chip size="small" label={`${history.runs} run${history.runs === 1 ? '' : 's'}`} />
        <Chip
          size="small"
          color={DIRECTION_STYLES[history.direction].color}
          label={`${DIRECTION_STYLES[history.direction].label} · ${formatDelta(history.delta)}`}
        />
        <Typography variant="caption" color="text.secondary">
          First scored {formatDateTime(history.firstSeenAt)} · dots coloured by risk band
        </Typography>
      </Stack>

      <Box sx={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={tickFormatter}
              tick={{ fontSize: AXIS_FONT_SIZE }}
              minTickGap={AXIS_MIN_TICK_GAP}
            />
            <YAxis domain={[0, SCORE_AXIS_MAX]} tick={{ fontSize: AXIS_FONT_SIZE }} />
            <Tooltip
              wrapperStyle={{ zIndex: 3 }}
              content={({ active, payload }) => {
                if (!active || !payload || payload.length === 0) {
                  return null;
                }

                const { point } = payload[0].payload as ChartPoint;
                const tier = bandToTier(point.band);

                return (
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="caption" color="text.secondary" component="p">
                      {formatDateTime(point.computedAt)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {point.score} · {tier}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="p">
                      Hard {point.hardScore} · LLM {point.llmScore ?? EMPTY_VALUE} ·{' '}
                      {point.trajectory ?? 'no trajectory'}
                      {point.divergent ? ' · divergent' : ''}
                    </Typography>
                  </Paper>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              name="Blended"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              isAnimationActive={false}
              // Each point takes its own band colour, so the run where a ticket turned critical stands out.
              dot={(dotProps) => {
                const { cx, cy, payload, index } = dotProps as {
                  cx: number;
                  cy: number;
                  index: number;
                  payload: ChartPoint;
                };

                return (
                  <circle
                    key={`score-dot-${index}`}
                    cx={cx}
                    cy={cy}
                    r={DOT_RADIUS}
                    fill={theme.palette[RISK_TIER_COLORS[bandToTier(payload.point.band)]].main}
                    stroke={theme.palette.background.paper}
                    strokeWidth={1}
                  />
                );
              }}
              activeDot={{ r: ACTIVE_DOT_RADIUS }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
}
